import intersection from 'lodash.intersection'
import isEmpty from 'lodash.isempty'
import pdfMake from 'pdfmake/build/pdfmake'
import * as pdfFonts from 'pdfmake/build/vfs_fonts'
import QRCode from 'qrcode'

import { simpleCopy } from '@/util/util'

import { KNOCKAUT_APP_LOGO } from './base64Images/appLogo'
import { DATASHEET_FOOTER } from './base64Images/footerImage'
import { KNOCKAUT_LOGO_HEADER } from './base64Images/knockautLogo'
import {
  DASHBOARD_CONFIGURATOR_MODULE_ID,
  KnockautObject,
  SymconObjectType,
} from './interfaces'
import { KnockautHandler } from './knockautHandler'
;(<any>pdfMake).addVirtualFileSystem(pdfFonts)

export interface IUpdateDashboard {
  name: string
  baseID: number | null
  password: string
  settingsPassword: string
}

export class DashboardHandler {
  private _knockautHandler: KnockautHandler
  private _knockautConfiguratorId: number

  constructor(
    knockautHandler: KnockautHandler,
    knockautConfiguratorId: number
  ) {
    this._knockautHandler = knockautHandler
    this._knockautConfiguratorId = knockautConfiguratorId
  }

  /**
   * Returns the settings password for a dashbaord
   * @param dashboardId id of dashboard
   * @returns password if found, otherwise null
   */
  async getDashboardSettingsPassword(
    dashboardId: number
  ): Promise<string | null> {
    const config = JSON.parse(
      await this._knockautHandler.getConfiguration(this._knockautConfiguratorId)
    )
    let dashboards = []
    if (config.WebFronts) {
      dashboards = JSON.parse(config.WebFronts)
    }
    for (const dashboard of dashboards) {
      if (dashboard.webFront === dashboardId) {
        return dashboard.password
      }
    }
    return null
  }

  /**
   * Sets the settings password for a dashbaord
   * @param dashboardId id of dashboard
   * @param password new password
   */
  async setDashboardSettingsPassword(
    dashboardId: number,
    password: string
  ): Promise<void> {
    const config = JSON.parse(
      await this._knockautHandler.getConfiguration(this._knockautConfiguratorId)
    )
    let dashboards = []
    if (config.WebFronts) {
      dashboards = JSON.parse(config.WebFronts)
    }
    let dashboardFound = false
    for (const dashboard of dashboards) {
      if (dashboard.webFront === dashboardId) {
        dashboardFound = true
        dashboard.password = password
      }
    }
    if (!dashboardFound) {
      throw new Error(`Could not find dashboard with id ${dashboardId}`)
    }
    config.WebFronts = JSON.stringify(dashboards)
    await this._knockautHandler.setConfigurationAndApply(
      this._knockautConfiguratorId,
      config
    )
  }

  async createDashboard(
    name: string = 'Dashboard',
    password: string = '0000'
  ): Promise<number> {
    const baseID = await this._knockautHandler.createCategoryRpc({
      name: name,
      parentID: null,
    })
    const id = await this._knockautHandler.parseKnockautResponse(
      'IPS_CreateInstance',
      [DASHBOARD_CONFIGURATOR_MODULE_ID]
    )
    await this._knockautHandler.parseKnockautResponse('IPS_SetName', [id, name])

    // Initialize config
    let config = JSON.parse(await this._knockautHandler.getConfiguration(id))
    config.Skin = 'KNOCKAUT_DI_Skin'
    config.Items = JSON.stringify([
      {
        ClassName: 'ClockWidget',
        Configuration: '',
        ID: 'clock',
        ParentID: 'roottp',
        Position: 0,
        Visible: true,
      },
      {
        ClassName: 'Category',
        Configuration: `{"baseID":${baseID}}`,
        ID: 'root',
        ParentID: 'roottp',
        Position: 0,
        Visible: true,
      },
      {
        ClassName: 'TabPane',
        Configuration: '{"subTitle":"IP-Symcon", "subIcon":"IPS"}',
        ID: 'roottp',
        ParentID: '',
        Position: 0,
        Visible: true,
      },
      {
        ClassName: 'DWD',
        Configuration: '',
        ID: 'dwd',
        ParentID: 'roottp',
        Position: 1,
        Visible: true,
      },
    ])
    await this._knockautHandler.setConfigurationAndApply(
      id,
      JSON.stringify(config)
    )

    // Add default folders that are expected for KnockAut App
    const defaultFolders = [
      'Dashboard',
      'SystemAlerts',
      'SystemFooter',
      'SystemScripts',
    ]
    for (const folderName of defaultFolders) {
      const id = await this._knockautHandler.createCategoryRpc({
        name: folderName,
        parentID: baseID,
      })
      await this._knockautHandler.setHidden(id, true)
    }

    // Add to configurator
    config = JSON.parse(
      await this._knockautHandler.getConfiguration(this._knockautConfiguratorId)
    )
    let dashboards = []
    if (config.WebFronts) {
      dashboards = JSON.parse(config.WebFronts)
    }
    dashboards.push({
      name,
      password,
      webFront: id,
    })
    config.WebFronts = JSON.stringify(dashboards)
    await this._knockautHandler.setConfigurationAndApply(
      this._knockautConfiguratorId,
      config
    )

    await this._knockautHandler.refreshTree()
    return id
  }

  /**
   * Delete a dashbaord and remove from KnockAut Configurator
   * @param id ID of the visualization instance
   */
  async deleteDashboard(id: number): Promise<void> {
    // Delete the "visualization" instance
    await this._knockautHandler.deleteSymconObject(
      SymconObjectType.INSTANCE.valueOf(),
      id
    )

    // Delete from configurator
    const config = JSON.parse(
      await this._knockautHandler.getConfiguration(this._knockautConfiguratorId)
    )
    let dashboards = []
    if (config.WebFronts) {
      dashboards = JSON.parse(config.WebFronts)
    }
    const ind = dashboards.findIndex((webFront) => webFront.webFront === id)
    if (ind > -1) {
      dashboards.splice(ind, 1)
    }
    config.WebFronts = JSON.stringify(dashboards)
    await this._knockautHandler.setConfigurationAndApply(
      this._knockautConfiguratorId,
      config
    )
  }

  /**
   * Updates a dashboard instance and corresponding root folder
   * @param instanceId The ID of the dashboard instance
   * @param update update data
   */
  async updateDashboard(instanceId: number, update: IUpdateDashboard) {
    const instance = await this._knockautHandler.getObjectById(instanceId)
    if (instance.name !== update.name) {
      // Keep name of dashboard instance and root folder the same
      await this._knockautHandler.setNameSymconObject(instanceId, update.name)
      await this._knockautHandler.setNameSymconObject(
        update.baseID,
        update.name
      )
    }
    const config = JSON.parse(
      await this._knockautHandler.getConfiguration(instanceId)
    )
    const configItems = JSON.parse(config.Items)
    for (const configItem of configItems) {
      if (configItem.ClassName === 'Category') {
        const config = JSON.parse(configItem.Configuration)
        if (update.baseID) {
          config.baseID = update.baseID
        }
        configItem.Configuration = JSON.stringify(config)
      }
    }
    const newConfigItems = JSON.stringify(configItems)
    config.Items = newConfigItems
    config.Password = update.password

    await this._knockautHandler.setConfigurationAndApply(
      instanceId,
      JSON.stringify(config)
    )

    await this.setDashboardSettingsPassword(instanceId, update.settingsPassword)

    this._knockautHandler.refreshTree()
  }

  async createDataSheet(dashboard: KnockautObject) {
    const config = JSON.parse(
      await this._knockautHandler.getConfiguration(dashboard.id)
    )
    const password = config.Password
    let mobileLink = `symcons://${this._knockautHandler.project.remote_access_link}/#${dashboard.id}`
    const tileVisuModuleID = '{B5B875BB-9B76-45FD-4E67-2607E45B3AC4}'
    if (dashboard.data.moduleID === tileVisuModuleID) {
      const accessURLs = [this._knockautHandler.project.remote_access_link]
      if (this._knockautHandler.project.system_information) {
        const system_info = JSON.parse(
          this._knockautHandler.project.system_information
        )
        const localIP = system_info.network[0].IP
        if (localIP) {
          accessURLs.push(localIP)
        }
      }
      const encodedURLs = encodeURI(JSON.stringify(accessURLs))
      mobileLink = `symconvisu://${this._knockautHandler.project.remote_access_link}?servers=${encodedURLs}/#${dashboard.id}`
    }
    const qrcode = await QRCode.toDataURL(mobileLink)
    const datasheet = simpleCopy(dashboardDatasheetPdf)
    const subscription =
      await this._knockautHandler.utilControlHandler.getLicenseSubscriptionDate()

    // index 7 = First table
    datasheet.content[7].table.body[1][0].text = dashboard.name
    datasheet.content[7].table.body[1][1].image = qrcode
    datasheet.content[7].table.body[1][2].text = password
    // index 8 = Second table
    datasheet.content[8].table.body[0][1] =
      this._knockautHandler.project.remote_access_link
    datasheet.content[8].table.body[1][1] =
      this._knockautHandler.project.username
    if (subscription) {
      datasheet.content[8].table.body[2][1] = subscription.toLocaleString()
    } else {
      datasheet.content[8].table.body[2][1] =
        'Subskription konnte nicht gelesen werden.'
    }
    const dd = {
      pageSize: 'A4',
      pageMargins: [70, 40, 70, 0],
      ...datasheet,
      styles: {
        tableExample: {
          margin: [0, 5, 0, 15],
        },
      },
      defaultStyle: {
        fontSize: 10,
      },
    }
    // Following line can be used for debugging in http://pdfmake.org/playground.html
    // console.log(JSON.stringify(dd, null, 2))
    const pdf = pdfMake.createPdf(dd)
    pdf.download(`${dashboard.name}-Datenblatt.pdf`)
  }

  /**
   * Returns true if obj is inside a folder that is the root of a dashboard
   */
  isInsideDashboard(obj: KnockautObject): boolean {
    return !isEmpty(
      intersection(obj.parentPath, this._knockautHandler.dashboardBaseIds)
    )
  }

  /**
   * Returns true if obj is the root folder of a dashboard
   */
  isDashboardBaseFolder(id: number): boolean {
    return this._knockautHandler.dashboardBaseIds.includes(id)
  }

  /**
   * Returns baseID of dashboard (usually set in DashboardEditor)
   * @param dashboard
   */
  getDashboardBaseID(dashboard: KnockautObject): number {
    let baseID = null
    if (dashboard.data.configuration.Items) {
      const configItems = JSON.parse(dashboard.data.configuration.Items)
      for (const configItem of configItems) {
        if (configItem.ClassName === 'Category') {
          const config = JSON.parse(configItem.Configuration)
          baseID = config.baseID
        }
      }
    }
    return baseID
  }

  /**
   * Returns baseID of dashboard (usually set in DashboardEditor)
   * @param dashboard
   */
  getDashboardIdFromBaseID(baseId: number): number {
    for (const dashboard of this._knockautHandler.dashboards) {
      if (dashboard.data.configuration.Items) {
        const configItems = JSON.parse(dashboard.data.configuration.Items)
        for (const configItem of configItems) {
          if (configItem.ClassName === 'Category') {
            const config = JSON.parse(configItem.Configuration)
            if (config.baseID === baseId) {
              return dashboard.id
            }
          }
        }
      }
    }
    return null
  }
}

const dashboardDatasheetPdf = {
  content: [
    {
      columns: [
        {
          image: KNOCKAUT_LOGO_HEADER,
          margin: [0, 0, 0, 0],
          width: 200,
        },
      ],
    },
    {
      text: 'Sehr geehrte Kundin',
      margin: [0, 30, 0, 0],
    },
    {
      text: 'Sehr geehrter Kunde',
      margin: [0, 2, 0, 0],
    },
    {
      text: 'Herzlichen Dank, dass Sie sich für das KNOCKAUT® TITAN – System entschieden haben.',
      margin: [0, 20, 0, 0],
    },
    {
      text: 'Bevor es losgehen kann, sind noch folgende Punkte zu erledigen:',
      margin: [0, 15, 0, 0],
    },
    {
      columns: [
        {
          image: KNOCKAUT_APP_LOGO,
          margin: [0, 25, 0, 0],
          width: 20,
        },
        {
          text: 'APP downloaden und einloggen',
          margin: [10, 30, 0, 0],
          style: {
            bold: true,
          },
        },
      ],
    },
    {
      text: 'Laden Sie per Smartphone oder Tablet die „Knockaut Titan MAX“-APP herunter, scannen Sie den QR-Code, und melden Sie sich mit folgenden persönlichen Benutzerdaten an:',
      margin: [0, 10, 0, 0],
      style: {
        italics: true,
      },
    },
    {
      style: 'tableExample',
      margin: [10, 15, 0, 10],
      table: {
        widths: [120, '*', 100],
        body: [
          ['Dashboard-Name', 'QR-Code', 'Passwort'],
          [
            {
              text: 'TODO',
              alignment: 'center',
            },
            {
              image: 'TODO',
              margin: [0, 0, 0, 0],
              width: 150,
            },
            {
              text: 'TODO',
              alignment: 'center',
            },
          ],
        ],
      },
    },
    {
      style: 'tableExample',
      margin: [10, 15, 0, 10],
      table: {
        widths: [120, '*'],
        body: [
          ['Browser-Link', 'TODO'],
          ['Lizenzname', 'TODO'],
          ['Connect & Care gültig bis:', 'TODO'],
        ],
      },
    },
    {
      text: 'Hinweise:',
      margin: [0, 70, 0, 0],
      style: {
        bold: true,
      },
    },
    {
      text: '-	Bitte beachten Sie, dass per Standard der Fernzugriff (Connect-Dienst) aktiviert ist.',
      margin: [5, 2, 0, 0],
    },
    {
      text: '-	Der Fernzugriff läuft nur mit gültigem Connect & Care-Dienst (minimale jährliche Kosten)',
      margin: [5, 2, 0, 0],
    },
    {
      text: '-	Bitte bewahren Sie die oben genannten Passwörter an einem sicheren Ort auf und machen diese Unberechtigten nicht zugänglich.',
      margin: [5, 2, 0, 0],
    },
    {
      text: 'Dann wünschen wir Ihnen mit dem KNOCKAUT® TITAN – System viel Freude und sind bei Fragen oder Unklarheiten telefonisch (+41 56 500 18 18) oder per E-Mail (info@brelag.com) sehr gerne für Sie da.',
      margin: [0, 15, 0, 0],
    },
    {
      text: 'Ihr Brelag Team',
      margin: [0, 20, 0, 0],
    },
    {
      columns: [
        {
          image: DATASHEET_FOOTER,
          margin: [0, 30, 0, 0],
          width: 200,
        },
      ],
    },
  ],
}
